import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Mail, ChevronRight, Filter, Search, Package, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import SEO from '../components/SEO';
import { formatPrice } from '../utils/formatPrice';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryData = {
  'filtres': {
    title: 'Filtres & Cartouches pour Spa',
    metaTitle: 'Filtres et Cartouches pour Spa - Pièces détachées',
    metaDescription: 'Filtres et cartouches de filtration pour spas. Stock disponible, livraison rapide France. Bien vérifier les dimensions avant commande.',
    h1: 'Filtres & Cartouches pour Spa',
    intro: 'Découvrez notre sélection de filtres et cartouches de filtration pour spas. Un filtre propre garantit une eau saine et prolonge la durée de vie de votre spa. Bien vérifier les dimensions et références avant commande.',
    icon: '🔧',
    apiCategory: 'filtres'
  },
  'pompes': {
    title: 'Pompes pour Spa',
    metaTitle: 'Pompes pour Spa - Pompes de circulation et jets',
    metaDescription: 'Pompes de circulation et pompes jets pour spas. Pièces détachées de qualité. Livraison France. Contactez-nous avant commande en cas de doute.',
    h1: 'Pompes pour Spa',
    intro: 'Pompes de circulation, pompes jets et pompes de massage pour votre spa. Nous sélectionnons des pompes de qualité professionnelle. Installation possible par notre technicien. Bien vérifier les références avant commande.',
    icon: '💧',
    apiCategory: 'pompes'
  },
  'chauffages': {
    title: 'Chauffages pour Spa',
    metaTitle: 'Chauffages pour Spa - Réchauffeurs électriques',
    metaDescription: 'Réchauffeurs et résistances de chauffage pour spas. Pièces détachées de qualité, livraison rapide France. SAV local Nantes et Grand Ouest.',
    h1: 'Chauffages & Réchauffeurs pour Spa',
    intro: 'Réchauffeurs électriques, résistances et éléments de chauffage pour spas. Des pièces essentielles pour maintenir votre eau à température idéale. Diagnostic et installation disponibles.',
    icon: '🔥',
    apiCategory: 'chauffages'
  },
  'couvertures': {
    title: 'Couvertures pour Spa',
    metaTitle: 'Couvertures Isothermes pour Spa - Protection et isolation',
    metaDescription: 'Couvertures isothermes et bâches de protection pour spas. Sur mesure ou standard. Livraison et installation Grand Ouest.',
    h1: 'Couvertures Isothermes pour Spa',
    intro: 'Couvertures isothermes, bâches de protection et housses pour spas. Indispensables pour conserver la chaleur, protéger votre spa et réduire votre consommation énergétique. Disponibles sur mesure.',
    icon: '🛡️',
    apiCategory: 'couvertures'
  },
  'produits-traitement': {
    title: 'Produits de Traitement pour Spa',
    metaTitle: 'Produits de Traitement Eau Spa - Chlore, Brome, pH',
    metaDescription: 'Produits de traitement pour eau de spa : chlore, brome, pH, anti-calcaire. Conseils d\'entretien par un technicien spécialiste.',
    h1: 'Produits de Traitement de l\'Eau',
    intro: 'Chlore, brome, régulateurs de pH, anti-calcaire et produits d\'entretien pour l\'eau de votre spa. Nous vous conseillons sur les dosages et la fréquence de traitement adaptés à votre utilisation.',
    icon: '🧪',
    apiCategory: 'produits-traitement'
  },
  'cartes-electroniques': {
    title: 'Cartes Électroniques pour Spa',
    metaTitle: 'Cartes Électroniques Spa - Balboa, Gecko, Pièces détachées',
    metaDescription: 'Cartes électroniques et circuits de contrôle pour spas Balboa, Gecko. Diagnostic et remplacement par technicien spécialiste.',
    h1: 'Cartes Électroniques & Circuits de Contrôle',
    intro: 'Cartes électroniques, circuits de contrôle et pièces de rechange pour systèmes Balboa, Gecko et autres. Diagnostic précis et remplacement par notre technicien certifié. Pièces d\'origine ou compatibles.',
    icon: '⚡',
    apiCategory: 'cartes-electroniques'
  }
};

const allCategories = [
  { slug: 'filtres', label: 'Filtres & Cartouches' },
  { slug: 'pompes', label: 'Pompes' },
  { slug: 'chauffages', label: 'Chauffages' },
  { slug: 'couvertures', label: 'Couvertures' },
  { slug: 'produits-traitement', label: 'Produits de traitement' },
  { slug: 'cartes-electroniques', label: 'Cartes électroniques' },
];

const AccessoiresCategoryPage = () => {
  const { category } = useParams();
  const [accessories, setAccessories] = useState([]);
  const [filteredAccessories, setFilteredAccessories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const catData = categoryData[category] || categoryData['filtres'];

  useEffect(() => {
    const fetchAccessories = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/accessories`, {
          params: { category: catData.apiCategory }
        });
        setAccessories(response.data);
        setFilteredAccessories(response.data);
      } catch (error) {
        console.error('Error fetching accessories:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAccessories();
  }, [category, catData.apiCategory]);

  useEffect(() => {
    if (search) {
      const searchLower = search.toLowerCase();
      setFilteredAccessories(accessories.filter(acc => 
        acc.name.toLowerCase().includes(searchLower) ||
        acc.reference.toLowerCase().includes(searchLower) ||
        (acc.compatibility && acc.compatibility.toLowerCase().includes(searchLower))
      ));
    } else {
      setFilteredAccessories(accessories);
    }
  }, [search, accessories]);

  // BreadcrumbList JSON-LD
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://atlantiquespaboutique.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Accessoires & Pièces",
        "item": "https://atlantiquespaboutique.com/accessoires"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": catData.title,
        "item": `https://atlantiquespaboutique.com/accessoires/${category}`
      }
    ]
  };

  // ItemList JSON-LD for products
  const itemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": catData.title,
    "description": catData.metaDescription,
    "numberOfItems": filteredAccessories.length,
    "itemListElement": filteredAccessories.map((acc, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": acc.name,
        "description": acc.description,
        "sku": acc.reference,
        "brand": {
          "@type": "Brand",
          "name": "Atlantique Spa Boutique"
        },
        "offers": {
          "@type": "Offer",
          "availability": acc.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "price": acc.price || 0,
          "priceCurrency": "EUR",
          "seller": {
            "@type": "Organization",
            "name": "Atlantique Spa Boutique"
          }
        }
      }
    }))
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`accessoires-${category}-page`}>
      <SEO 
        title={catData.metaTitle}
        description={catData.metaDescription}
        canonical={`/accessoires/${category}`}
      />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/accessoires">Accessoires & Pièces</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{catData.title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          <Link to="/accessoires" className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 mb-4">
            <ArrowLeft className="w-4 h-4" />
            Toutes les catégories
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            {catData.h1}
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl">
            {catData.intro}
          </p>
          <p className="text-sm text-slate-500 mt-4 italic">
            Livraison, installation et mise en service réalisées par un technicien spécialiste, sur devis selon votre configuration.
          </p>
        </div>
      </div>

      {/* Search & Category Navigation */}
      <div className="bg-white border-b border-slate-200 sticky top-16 lg:top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Rechercher dans cette catégorie..." 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="category-search-input"
              />
            </div>
            <div className="ml-auto text-sm text-slate-500">
              {filteredAccessories.length} article{filteredAccessories.length > 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Other Categories */}
      <div className="bg-slate-100 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex gap-2 overflow-x-auto hide-scrollbar">
            {allCategories.map(cat => (
              <Link key={cat.slug} to={`/accessoires/${cat.slug}`}>
                <Button
                  variant={category === cat.slug ? "default" : "outline"}
                  size="sm"
                  className="flex-shrink-0"
                >
                  {cat.label}
                </Button>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1,2,3,4,5,6,7,8].map(i => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-4">
                  <div className="h-4 bg-slate-200 rounded mb-2" />
                  <div className="h-4 bg-slate-200 rounded w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredAccessories.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              Aucun article dans cette catégorie
            </h3>
            <p className="text-slate-600 mb-6">
              Contactez-nous pour toute question sur la pièce recherchée.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:contact@atlantiquespaboutique.com">
                <Button className="bg-orange-500 hover:bg-orange-600 gap-2">
                  <Mail className="w-5 h-5" />
                  Nous contacter
                </Button>
              </a>
              <Link to="/accessoires">
                <Button variant="outline">Voir toutes les catégories</Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredAccessories.map((acc) => (
              <Card key={acc.id} className="card-hover border-slate-200 bg-white" data-testid={`accessory-card-${acc.slug}`}>
                <CardContent className="p-5">
                  <div className="text-xs text-orange-600 uppercase tracking-wide mb-1 font-medium">
                    {catData.title}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{acc.name}</h3>
                  <div className="text-sm text-slate-600 mb-3">
                    <div>Réf: <span className="font-mono">{acc.reference}</span></div>
                    {acc.compatibility && (
                      <div className="text-slate-500">Compatible: {acc.compatibility}</div>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">{acc.description}</p>
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    {acc.price_on_request ? (
                      <span className="text-orange-600 font-medium">Sur demande</span>
                    ) : (
                      <span className="text-xl font-bold text-slate-900">{formatPrice(acc.price)}</span>
                    )}
                    {acc.in_stock && (
                      <span className="badge-stock text-xs">En stock</span>
                    )}
                  </div>
                  <div className="mt-4 flex gap-2">
                    <a href="mailto:contact@atlantiquespaboutique.com" className="flex-1">
                      <Button variant="outline" size="sm" className="w-full gap-1">
                        <Mail className="w-4 h-4" />
                        Une question ?
                      </Button>
                    </a>
                    <Link to={`/devis?piece=${acc.slug}`} className="flex-1">
                      <Button size="sm" className="w-full bg-orange-500 hover:bg-orange-600">
                        Commander
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="bg-slate-900 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Vous ne trouvez pas votre pièce ?
          </h2>
          <p className="text-slate-300 mb-6">
            Contactez-nous avec la référence ou une photo. Notre technicien trouvera la pièce compatible.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:contact@atlantiquespaboutique.com">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 gap-2">
                <Mail className="w-5 h-5" />
                Nous contacter
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-slate-800">
                Demande de pièce
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(breadcrumbJsonLd)
      }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(itemListJsonLd)
      }} />
    </div>
  );
};

export default AccessoiresCategoryPage;
